/**
 * WizardShell — chrome for the seller registration wizard.
 *
 * Mirrors `OnbShell` from `features/onboarding/OnboardingFlow.tsx`:
 *   - Back button (left, hidden on entry step)
 *   - StepDots (center, 4 dots for Profile / Email-OTP / Phone-OTP / Review)
 *   - The step content below in a width-capped column
 *
 * The wizard has 4 visible steps in StepDots (the Start email-entry screen
 * is the entry point, not counted in the dots). aria-label on StepDots per
 * the i18n key `stepAria`.
 *
 * Tokens: `--g-*` only. 44px tap targets. prefers-reduced-motion safe.
 */
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Icon, StepDots } from "@garden/ui";

import { ThemeToggle } from "@/components/ThemeToggle";
import { Fade } from "@/components/motion";
import { pressClass } from "@/components/motion/Pressable";

import {
  SELLER_REG_NS,
  registerSellerRegI18n,
} from "@/features/seller-registration/api";
import {
  WIZARD_STEPS,
  stepIndex,
  type WizardStep,
} from "@/features/seller-registration/schemas";

export interface WizardShellProps {
  step: WizardStep;
  onBack: () => void;
  hideBack?: boolean;
  children: ReactNode;
}

export function WizardShell({
  step,
  onBack,
  hideBack = false,
  children,
}: WizardShellProps): ReactElement {
  registerSellerRegI18n();
  const { t } = useTranslation(SELLER_REG_NS);

  const current = stepIndex(step);
  const total = WIZARD_STEPS.length;

  return (
    <div
      data-testid="seller-wizard"
      data-step={step}
      className="flex min-h-dvh flex-col bg-[var(--g-ground)]"
    >
      <header className="flex items-center justify-between px-5 py-4">
        <button
          type="button"
          data-testid="wizard-back"
          aria-label={t("cta.back")}
          onClick={onBack}
          className={[
            "grid h-[44px] w-[44px] place-items-center rounded-[var(--g-radius)]",
            "bg-[var(--g-surface)] text-foreground shadow-[var(--g-shadow-sm)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-ground)]",
            hideBack ? "invisible" : "",
            pressClass("cta"),
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Icon name="chevL" size={18} />
        </button>
        <StepDots
          current={current}
          total={total}
          aria-label={t("stepAria", { current, total })}
        />
        {/* Light/dark toggle (also balances the back button so StepDots stays
            centered — same 44px footprint as the back control). */}
        <ThemeToggle />
      </header>
      <main className="mx-auto flex w-full max-w-[480px] flex-1 flex-col px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2">
        <Fade key={step} className="flex flex-1 flex-col">
          <>{children}</>
        </Fade>
      </main>
    </div>
  );
}
